import { useEffect, useRef } from 'react';
import { claimReferral } from '@packages/api/endpoints/referral.js';
import { useAccount } from '../lib/account/useAccount';
import { clearReferralCode, readReferralCode } from '../lib/referral-capture';

/** Mirrors the backend's claim window; past it the server records nothing anyway. */
const CLAIM_WINDOW_MS = 24 * 60 * 60 * 1000;

/**
 * Claims a captured referral code for the current account once per load, if
 * the user owns an account created within the claim window. An administered
 * entity is skipped, as the server would refuse it, and the code is kept for a
 * practice of the user's own.
 *
 * Mounted in the authenticated layout rather than in onboarding, so a claim
 * that failed on the way through (network, a closed tab) is retried on the
 * next load while the window is open. Any answer from the server settles it
 * and clears the code, whether or not a referral was recorded; a failure keeps
 * it for the next load. Never surfaces an error: a referral must not get in
 * the way of using Bill.
 */
export function useReferralClaim(): void {
  const { account, role, administeredBy } = useAccount();
  const attemptedForRef = useRef<string | null>(null);

  useEffect(() => {
    if (!account || role !== 'owner' || administeredBy) return;
    if (attemptedForRef.current === account.id) return;
    if (Date.now() - new Date(account.createdAt).getTime() > CLAIM_WINDOW_MS) return;
    const code = readReferralCode();
    if (!code) return;

    attemptedForRef.current = account.id;
    claimReferral(account.id, { code, idempotencyKey: crypto.randomUUID() })
      .then(() => clearReferralCode())
      .catch(() => {
        // Kept for the next load; the server dedupes a repeat claim for the account.
      });
  }, [account, role, administeredBy]);
}
